'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import {
  Eye, EyeOff, ArrowRight, CheckCircle, Shield, User, Stethoscope, Building2, Settings, Copy, Check
} from 'lucide-react';

type AuthMode = 'login' | 'signup';

type UserRole = 'patient' | 'physician' | 'staff' | 'admin';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  agreeTerms: boolean;
}

const roles: { id: UserRole; label: string; description: string; icon: React.ElementType }[] = [
  { id: 'patient', label: 'Patient', description: 'Access your care plan & appointments', icon: User },
  { id: 'physician', label: 'Physician', description: 'Submit referrals & track patients', icon: Stethoscope },
  { id: 'staff', label: 'Clinic Staff', description: 'Manage clinic operations', icon: Building2 },
  { id: 'admin', label: 'Admin', description: 'Full network administration', icon: Settings },
];

const demoCredentials: { role: string; email: string; password: string }[] = [
  { role: 'Patient', email: 'sarah.k@ashapatient.com', password: 'PatientDemo2026!' },
  { role: 'Physician', email: 'dr.patel@ashareferral.com', password: 'PhysicianDemo2026!' },
  { role: 'Clinic Staff', email: 'j.torres@ashastaff.com', password: 'StaffDemo2026!' },
  { role: 'Admin', email: 'ops@asharecovery.com', password: 'AdminDemo2026!' },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('staff');
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const router = useRouter();

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'staff',
      agreeTerms: false,
    },
  });

  const handleLoginSubmit = async (data: LoginForm) => {
    // Backend integration point: POST /api/auth/login
    const validCreds = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!validCreds) {
      loginForm.setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setLoading(false);
    toast.success(`Welcome back! Signed in as ${validCreds.role}`);
    router.push('/dashboard-overview');
  };

  const handleSignupSubmit = async (data: SignupForm) => {
    // Backend integration point: POST /api/auth/register
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    toast.success('Account created! Redirecting to your dashboard...');
    router.push('/dashboard-overview');
  };

  const autofillCredentials = (cred: { email: string; password: string }) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    if (mode === 'signup') setMode('login');
    toast.info('Credentials filled — click Sign In to continue');
  };

  const copyToClipboard = (value: string, key: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 relative overflow-hidden"
        style={{ background: 'var(--primary)' }}>
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-5 bg-white" />
        <div className="absolute bottom-20 -left-10 w-48 h-48 rounded-full opacity-5 bg-white" />
        <div className="absolute top-1/2 right-0 w-32 h-32 rounded-full opacity-5 bg-secondary" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <img
            src="/assets/images/LOGOvertical-1777629360990.png"
            alt="ASHA Recovery Group"
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Center Content */}
        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4">
              Your Recovery Network,{' '}
              <span style={{ color: 'var(--secondary)' }}>Connected.</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-sm">
              Access your care plan, submit referrals, and manage clinic operations — all from one secure platform.
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-4">
            {[
              'Real-time referral status tracking',
              'Appointment scheduling & management',
              'Secure patient progress reports',
              'Multi-clinic operations dashboard',
            ].map((feature) => (
              <div key={`feature-${feature}`} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(27,175,191,0.2)' }}>
                  <CheckCircle size={14} style={{ color: 'var(--secondary)' }} />
                </div>
                <span className="text-white/70 text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[
              { value: '18', label: 'Clinics' },
              { value: '2,847', label: 'Active Patients' },
              { value: '94%', label: 'Satisfaction' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="rounded-2xl p-4"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-2xl font-extrabold text-white tabular-nums">{stat.value}</p>
                <p className="text-white/50 text-xs mt-1 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* HIPAA Badge */}
        <div className="flex items-center gap-2.5 relative z-10">
          <Shield size={16} style={{ color: 'var(--secondary)' }} />
          <span className="text-white/50 text-xs">HIPAA Compliant · 256-bit Encrypted · SOC 2 Type II</span>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-10 bg-white overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <img
              src="/assets/images/LOGOHORIZINTAL-1777629360782.png"
              alt="ASHA Recovery Group"
              className="h-9 w-auto object-contain"
            />
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-muted rounded-2xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                mode === 'login' ? 'bg-white card-shadow text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                mode === 'signup' ? 'bg-white card-shadow text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Heading */}
          <div className="mb-7">
            <h1 className="text-2xl font-extrabold text-primary">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1.5">
              {mode === 'login' ?'Sign in to access your ASHA dashboard' :'Join the ASHA Recovery network'}
            </p>
          </div>

          {/* ─── LOGIN FORM ─── */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="input-field"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-danger font-medium">
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" className="block text-sm font-semibold text-foreground">
                    Password
                  </label>
                  <a href="#" className="text-xs font-medium hover:underline" style={{ color: 'var(--secondary)' }}>
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="input-field pr-11"
                    {...loginForm.register('password', { required: 'Password is required' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-danger font-medium">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2.5">
                <input
                  id="remember"
                  type="checkbox"
                  className="w-4 h-4 rounded accent-secondary"
                  {...loginForm.register('remember')}
                />
                <label htmlFor="remember" className="text-sm text-muted-foreground select-none cursor-pointer">
                  Remember me for 30 days
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 rounded-2xl text-base mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign In
                    <ArrowRight size={16} />
                  </span>
                )}
              </button>
            </form>
          )}

          {/* ─── SIGNUP FORM ─── */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-5">
              {/* Name row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-1.5">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="Sarah"
                    className="input-field"
                    {...signupForm.register('firstName', { required: 'First name is required' })}
                  />
                  {signupForm.formState.errors.firstName && (
                    <p className="mt-1.5 text-xs text-danger font-medium">{signupForm.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-1.5">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Kowalski"
                    className="input-field"
                    {...signupForm.register('lastName', { required: 'Last name is required' })}
                  />
                  {signupForm.formState.errors.lastName && (
                    <p className="mt-1.5 text-xs text-danger font-medium">{signupForm.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  className="input-field"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-danger font-medium">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Role Selector */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  I am a...
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map((role) => {
                    const IconComponent = role.icon;
                    return (
                      <button
                        key={`role-${role.id}`}
                        type="button"
                        onClick={() => {
                          setSelectedRole(role.id);
                          signupForm.setValue('role', role.id);
                        }}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all duration-150 ${
                          selectedRole === role.id
                            ? 'border-secondary bg-teal-light' :'border-border hover:border-secondary/40 hover:bg-muted/50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          selectedRole === role.id ? 'bg-secondary/20' : 'bg-muted'
                        }`}>
                          <IconComponent size={16} className={selectedRole === role.id ? 'text-secondary' : 'text-muted-foreground'} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-xs font-bold truncate ${selectedRole === role.id ? 'text-primary' : 'text-foreground'}`}>
                            {role.label}
                          </p>
                          <p className="text-2xs text-muted-foreground truncate">{role.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-1.5">
                  Password
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, one uppercase, one number</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    className="input-field pr-11"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-danger font-medium">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-foreground mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    className="input-field pr-11"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-danger font-medium">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2.5">
                <input
                  id="agreeTerms"
                  type="checkbox"
                  className="w-4 h-4 rounded accent-secondary mt-0.5 flex-shrink-0"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
                />
                <label htmlFor="agreeTerms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer select-none">
                  I agree to ASHA&apos;s{' '}
                  <a href="#" className="underline hover:text-secondary">Terms of Service</a>,{' '}
                  <a href="#" className="underline hover:text-secondary">Privacy Policy</a>, and acknowledge this platform handles protected health information (PHI) in accordance with HIPAA.
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-danger font-medium">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 rounded-2xl text-base disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Create Account
                    <ArrowRight size={16} />
                  </span>
                )}
              </button>
            </form>
          )}

          {/* Demo Credentials Box */}
          <div className="mt-8 rounded-2xl border border-border overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-2"
              style={{ background: 'var(--accent)' }}>
              <Shield size={14} style={{ color: 'var(--secondary)' }} />
              <span className="text-xs font-bold text-primary uppercase tracking-wider">Demo Accounts</span>
              <span className="text-xs text-muted-foreground ml-1">— click any row to autofill</span>
            </div>
            <div className="divide-y divide-border">
              {demoCredentials.map((cred) => (
                <div
                  key={`demo-${cred.role}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer group"
                  onClick={() => autofillCredentials(cred)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && autofillCredentials(cred)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xs font-bold px-2 py-0.5 rounded-md flex-shrink-0"
                      style={{ background: 'var(--teal-light)', color: 'var(--teal-mid)' }}>
                      {cred.role}
                    </span>
                    <span className="text-xs text-foreground font-medium truncate">{cred.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(cred.email, `email-${cred.role}`); }}
                      className="p-1 rounded-lg hover:bg-muted opacity-0 group-hover:opacity-100 transition-all"
                      aria-label="Copy email"
                    >
                      {copiedField === `email-${cred.role}` ? <Check size={12} className="text-success" /> : <Copy size={12} className="text-muted-foreground" />}
                    </button>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white"
                      style={{ background: 'var(--secondary)' }}>
                      Use
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Back to site */}
          <div className="mt-6 text-center">
            <Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Back to ASHA Recovery Group website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}